/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Exocet Header Motion — GSAP mega menu timelines
 */
(function () {
  "use strict";

  function supports() {
    return typeof gsap !== "undefined" && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function parseCascadeDelay(item, index) {
    const raw = item.style.getPropertyValue("--megamenu-cascade-delay").trim();
    if (raw) {
      const val = parseFloat(raw);
      if (Number.isFinite(val)) return val;
    }
    return 0.3 + index * 0.1;
  }

  function linkTargets(panel) {
    return panel.querySelectorAll(".megamenu__cascade-item");
  }

  function mediaTargets(panel) {
    return panel.querySelectorAll(
      ".megamenu__featured, .megamenu__promo, .megamenu__social, .megamenu__color-picker, .megamenu__info-box",
    );
  }

  function cascadeTargets(panel) {
    return panel.querySelectorAll(
      ".megamenu__cascade-item, .megamenu__featured, .megamenu__promo, .megamenu__social, .megamenu__color-picker, .megamenu__info-box",
    );
  }

  function resetCascade(panel) {
    gsap.set(linkTargets(panel), { xPercent: 0, opacity: 0, scale: 1 });
    gsap.set(mediaTargets(panel), { xPercent: 0, opacity: 0, scale: 0.985 });
  }

  function mediaCascadeDelay(item, index, linkCount) {
    const raw = item.style.getPropertyValue("--megamenu-cascade-delay").trim();
    if (raw) {
      const val = parseFloat(raw);
      if (Number.isFinite(val)) return val;
    }
    return 0.45 + linkCount * 0.1 + index * 0.12;
  }

  function createMegaMenuMotion(_ref) {
    const panels = _ref.panels;
    const overlay = _ref.overlay;
    const registry = new Map();
    let activeTimeline = null;
    let activeId = null;
    let pendingCloseResolve = null;

    function panelMotion(panel) {
      return {
        autoAlpha: 0,
        y: -10,
        visibility: "hidden",
        pointerEvents: "none",
      };
    }

    panels.forEach(function (panel) {
      const id = panel.dataset.megamenuPanel;
      if (!id) return;
      registry.set(id, panel);
      gsap.set(panel, panelMotion(panel));
      gsap.set(linkTargets(panel), { xPercent: 0, opacity: 0, scale: 1 });
      gsap.set(mediaTargets(panel), { xPercent: 0, opacity: 0, scale: 0.985 });
    });

    if (overlay) {
      gsap.set(overlay, { autoAlpha: 0, visibility: "visible", pointerEvents: "none" });
    }

    document.documentElement.classList.add("header-motion-ready");

    function killActive() {
      if (activeTimeline) {
        activeTimeline.kill();
        activeTimeline = null;
      }
      if (pendingCloseResolve) {
        pendingCloseResolve(null);
        pendingCloseResolve = null;
      }
    }

    function resetPanel(panel) {
      resetCascade(panel);
      gsap.set(panel, panelMotion(panel));
    }

    function buildOpenTimeline(panel, keepOverlay) {
      const linkItems = linkTargets(panel);
      const mediaItems = mediaTargets(panel);
      const tl = gsap.timeline({ paused: true, defaults: { overwrite: "auto" } });

      tl.set(panel, { visibility: "visible" }, 0);
      tl.fromTo(
        panel,
        { autoAlpha: 0, y: -10 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.5,
          ease: "power3.out",
          pointerEvents: "auto",
        },
        0,
      );

      if (overlay) {
        if (keepOverlay) {
          tl.set(overlay, { autoAlpha: 1, pointerEvents: "auto" }, 0);
        } else {
          tl.fromTo(
            overlay,
            { autoAlpha: 0 },
            { autoAlpha: 1, duration: 0.5, ease: "power2.inOut", pointerEvents: "auto" },
            0,
          );
        }
      }

      linkItems.forEach(function (item, index) {
        tl.fromTo(
          item,
          { xPercent: 0, opacity: 0 },
          { xPercent: 0, opacity: 1, duration: 1, ease: "circ.out" },
          parseCascadeDelay(item, index),
        );
      });

      mediaItems.forEach(function (item, index) {
        tl.fromTo(
          item,
          { xPercent: 0, opacity: 0, scale: 0.985 },
          { xPercent: 0, opacity: 1, scale: 1, duration: 0.85, ease: "expo.out" },
          mediaCascadeDelay(item, index, linkItems.length),
        );
      });

      return tl;
    }

    function buildCloseTimeline(panel, immediate, onDone) {
      const items = cascadeTargets(panel);
      const duration = immediate ? 0.15 : 0.32;
      const tl = gsap.timeline({
        paused: true,
        defaults: { overwrite: "auto" },
        onComplete: function () {
          resetPanel(panel);
          if (overlay) gsap.set(overlay, { autoAlpha: 0, pointerEvents: "none" });
          if (onDone) onDone();
        },
      });

      tl.to(
        items,
        {
          xPercent: 0,
          opacity: 0,
          scale: 0.985,
          duration: duration * 0.55,
          stagger: 0.015,
          ease: "power2.in",
        },
        0,
      );
      tl.to(
        panel,
        {
          autoAlpha: 0,
          y: -10,
          duration: duration,
          ease: "power2.in",
          pointerEvents: "none",
        },
        immediate ? 0 : 0.04,
      );

      if (overlay) {
        tl.to(overlay, { autoAlpha: 0, duration: duration, ease: "power2.in", pointerEvents: "none" }, 0);
      }

      return tl;
    }

    function open(panelId) {
      const panel = registry.get(panelId);
      if (!panel) return false;

      const switching = activeId !== null && activeId !== panelId;
      if (activeId === panelId && activeTimeline && activeTimeline.isActive()) return true;

      killActive();

      registry.forEach(function (otherPanel, id) {
        if (id !== panelId) resetPanel(otherPanel);
      });

      activeId = panelId;
      activeTimeline = buildOpenTimeline(panel, switching);
      activeTimeline.play(0);
      return true;
    }

    function closeImmediate() {
      killActive();
      registry.forEach(function (panel) {
        resetPanel(panel);
      });
      if (overlay) gsap.set(overlay, { autoAlpha: 0, pointerEvents: "none" });
      activeId = null;
    }

    function closeAnimated(immediate) {
      return new Promise(function (resolve) {
        if (!activeId) {
          resolve();
          return;
        }

        const panel = registry.get(activeId);
        if (!panel) {
          closeImmediate();
          resolve();
          return;
        }

        const closingId = activeId;
        killActive();
        activeId = null;

        activeTimeline = buildCloseTimeline(panel, !!immediate, function () {
          if (pendingCloseResolve) pendingCloseResolve(closingId);
        });
        pendingCloseResolve = function (result) {
          pendingCloseResolve = null;
          activeTimeline = null;
          resolve(result === undefined ? closingId : result);
        };
        activeTimeline.play(0);
      });
    }

    return {
      open: open,
      closeImmediate: closeImmediate,
      closeAnimated: closeAnimated,
      getActiveId: function () {
        return activeId;
      },
    };
  }

  window.ExocetHeaderMotion = {
    supports: supports,
    createMegaMenuMotion: createMegaMenuMotion,
  };
})();
